/** "End mission": ends the selected mission - final, no reopen - and starts its ePCR (the close-out).
 *  Sentio records it as an extra End Mission event from this page, marked as the page's (never the
 *  device's; sentio missionagent/integration/closeout/ending.py). Anyone who can see the mission may end it.
 *  A mission a simulation is still playing cannot be ended here: stop the simulation first (its Stop leaves the
 *  mission open), then end it.
 *  Once ended (from the device or here) it reads "Mission ended", with when and by whom.
 *  When the mission looks finished but nobody ended it (its device has sent nothing for a while; the
 *  dashboard's `end_suggestion`), a callout under the button asks whether to end it. "Not yet" hides it for a
 *  while. */

import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { useCookies } from "react-cookie";

import { endMission, errorMessage, fetchSimulation } from "../api";
import type { CloseoutResponse, EndReason, EndSuggestion } from "../types";
import { TOP_CONTROL_HEIGHT } from "./MissionPicker";

const hhmm = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const ACTIVE = ["queued", "running", "stopping"];

/** "Not yet" hides the callout for this long, per mission (remembered in this browser when it may store it). */
const SNOOZE_MS = 15 * 60 * 1000;
const snoozeKey = (missionId: string) => `missionAgent.endSnoozedUntil.${missionId}`;

function snoozedUntil(missionId: string): number {
  try {
    return Number(window.localStorage.getItem(snoozeKey(missionId))) || 0;
  } catch {
    return 0;
  }
}

/** 24-hour, as the rest of the page. */
const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });

/** "35 min", "2 h 10 min", "3 days". */
function since(iso: string, now: number): string {
  const minutes = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 48 * 60) return minutes % 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60} min` : `${minutes / 60} h`;
  return `${Math.round(minutes / 1440)} days`;
}

function reasonText(reason: EndReason, now: number): string {
  return `No device data since ${clock(reason.at)} (${since(reason.at, now)}).`;
}

/** "No device data since 09:40 (35 min)." */
function reasonsText(suggestion: EndSuggestion, now: number): string {
  return suggestion.reasons.map((reason) => reasonText(reason, now)).join(" ");
}

type EndMissionButtonProps = {
  deviceId: string;
  /** null while no engine-processed mission is selected. */
  missionId: string | null;
  /** The intervention id, for the dialog (and to tell whether a simulation is playing it). */
  incident: string;
  /** When and by whom the mission was ended (dashboard); null while it is not. */
  endedAt: string | null;
  endedBy: string | null;
  /** The mission looks finished but was never ended (dashboard); null otherwise. */
  suggestion?: EndSuggestion | null;
  /** Called with the answer once ended: the page re-reads the mission and the ePCR. */
  onEnded: (answer: CloseoutResponse) => void;
};

export function EndMissionButton(props: EndMissionButtonProps) {
  const { deviceId, missionId, incident, endedAt, endedBy, suggestion, onEnded } = props;
  const [cookies] = useCookies(["access_token"]);
  const token: string | undefined = cookies.access_token;
  const [confirming, setConfirming] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Ended here, before the dashboard has caught up.
  const [endedHere, setEndedHere] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [snoozed, setSnoozed] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setSnoozed(missionId ? snoozedUntil(missionId) : 0);
  }, [missionId]);
  useEffect(() => {
    // A snooze runs out without a page reload.
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  if (missionId === null || !missionId.startsWith("sentio:")) return null;

  const ended = endedAt ?? (endedHere && missionId === endedHere ? new Date().toISOString() : null);
  if (ended) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
        <Button size="small" variant="outlined" disabled sx={{ height: TOP_CONTROL_HEIGHT }}>
          Mission ended
        </Button>
      </Box>
    );
  }

  const open = async () => {
    setError(null);
    setPlaying(false);
    setConfirming(true);
    if (!token) return;
    try {
      const simulation = (await fetchSimulation(token, deviceId)).simulation;
      setPlaying(!!simulation && ACTIVE.includes(simulation.state) && simulation.intervention_id === incident);
    } catch {
      // Simulation is not available here (production): nothing can be playing.
    }
  };

  const snooze = () => {
    if (!missionId) return;
    const until = Date.now() + SNOOZE_MS;
    setSnoozed(until);
    try {
      window.localStorage.setItem(snoozeKey(missionId), String(until));
    } catch {
      // Not stored: hidden for this page load only.
    }
  };
  const asking = Boolean(suggestion?.reasons.length) && !confirming && !busy && now >= snoozed;

  const end = async () => {
    if (!token || !missionId) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await endMission(token, missionId);
      setEndedHere(missionId);
      setConfirming(false);
      onEnded(answer);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="contained"
        color="error"
        sx={{ height: TOP_CONTROL_HEIGHT }}
        disabled={busy}
        onClick={() => void open()}
        ref={setAnchor}
      >
        End mission
      </Button>
      <Popper
        open={asking && Boolean(anchor)}
        anchorEl={anchor}
        placement="bottom-end"
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
      >
        <Paper
          elevation={6}
          role="alertdialog"
          aria-label="Has this mission ended?"
          sx={{ p: 1.5, maxWidth: 340, borderLeft: 4, borderColor: "warning.main" }}
        >
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            Has this mission ended?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {suggestion ? reasonsText(suggestion, now) : null} End Mission has not been pressed.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.25 }}>
            <Button size="small" onClick={snooze}>
              Not yet
            </Button>
            <Button size="small" variant="contained" color="error" onClick={() => void open()}>
              End mission…
            </Button>
          </Box>
        </Paper>
      </Popper>
      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="sm">
        <DialogTitle>End mission {incident}?</DialogTitle>
        <DialogContent>
          {playing ? (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              A simulation is playing this mission. Stop it with Simulate first, then end the mission.
            </Alert>
          ) : null}
          <DialogContentText>
            Ending is final: the mission cannot be reopened. Sentio records an End Mission event from this page, marked
            as yours (not the device&apos;s), the mission is marked complete, and its ePCR is generated.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>
            Device data that arrives afterwards is still recorded, but the mission stays ended.
          </DialogContentText>
          {error ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy || playing} onClick={() => void end()}>
            End mission
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
